/**
 * Business Objective page: collect business objective and current state
 * in bridge card format (current | transition | future). Commit navigates to questions.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Form, Input, InputNumber, Button, Slider, Spin, Alert, Typography, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import {
  getBridgeSession,
  updateBridgeSession,
  type LocalBridgeSession,
} from '../services/localBridgeSessionApi';
import styles from '../components/BridgeCard/BridgeCard.module.css';
import pageStyles from './BusinessObjectivePage.module.css';
import { ImpactOfFrictionChart } from '../components/LocalBridge/ImpactOfFrictionChart';

const { TextArea } = Input;
const { Text } = Typography;
const DAYS_PER_MONTH = 30;

export const BusinessObjectivePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate = useNavigate();

  const [session, setSession] = useState<LocalBridgeSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [correctionFactorPercent, setCorrectionFactorPercent] = useState(0);
  const [form] = Form.useForm<{
    current_valuation?: number;
    current_revenue?: number;
    current_expense?: number;
    target_valuation?: number;
    bridge_objective?: string;
    estimated_months?: number;
    cost_one_hour_value?: number | null;
  }>();

  const loadSession = useCallback(async () => {
    if (!sessionId || !isLocalBridgeApiEnabled()) {
      setSession(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const s = await getBridgeSession(sessionId);
      setSession(s ?? null);
      if (s) {
        form.setFieldsValue({
          current_valuation: s.current_valuation ?? undefined,
          current_revenue: s.current_revenue ?? 0,
          current_expense: s.current_expense ?? 0,
          target_valuation: s.target_valuation ?? undefined,
          bridge_objective: s.bridge_objective ?? undefined,
          estimated_months: s.target_months ?? 12,
          cost_one_hour_value: s.commitment_toll_cost_1hr ?? undefined,
        });
      }
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId, form]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const currentV = Form.useWatch('current_valuation', form) ?? 0;
  const futureV = Form.useWatch('target_valuation', form) ?? 0;
  const months = Form.useWatch('estimated_months', form) ?? 0;
  const currentRevenue = Form.useWatch('current_revenue', form) ?? 0;
  const currentExpense = Form.useWatch('current_expense', form) ?? 0;

  const valueRatio = useMemo(() => {
    if (!currentRevenue || !currentExpense) return null;
    return currentRevenue / currentExpense;
  }, [currentRevenue, currentExpense]);

  const currentEarnings = useMemo(() => {
    const rev = currentRevenue ?? 0;
    const exp = currentExpense ?? 0;
    return rev - exp;
  }, [currentRevenue, currentExpense]);

  const currentMultiple = useMemo(() => {
    if (!currentEarnings || currentEarnings <= 0) return null;
    const val = currentV ?? 0;
    return val > 0 ? val / currentEarnings : null;
  }, [currentV, currentEarnings]);

  const correctionFactor = correctionFactorPercent / 100;

  const { futureRevenue, futureExpense, futureValueRatio, futureEarnings, futureMultiple } =
    useMemo<
      {
        futureRevenue: number;
        futureExpense: number;
        futureValueRatio: number | null;
        futureEarnings: number;
        futureMultiple: number | null;
      }
    >(() => {
      const rev = currentRevenue ?? 0;
      const exp = currentExpense ?? 0;
      const futureRev = rev + rev * correctionFactor;
      const futureExp = exp - exp * correctionFactor;
      const ratio = futureExp > 0 ? futureRev / futureExp : null;
      const earnings = futureRev - futureExp;
      const mult = earnings > 0 && futureV ? futureV / earnings : null;
      return {
        futureRevenue: futureRev,
        futureExpense: futureExp,
        futureValueRatio: ratio,
        futureEarnings: earnings,
        futureMultiple: mult,
      };
    }, [currentRevenue, currentExpense, correctionFactor, futureV]);

  const valuePerDay = useMemo(() => {
    if (!months || months <= 0) return null;
    const totalDays = months * DAYS_PER_MONTH;
    const delta = (futureV || 0) - (currentV || 0);
    return delta / totalDays;
  }, [months, futureV, currentV]);

  const valuePerHour = useMemo(() => {
    if (valuePerDay == null) return null;
    return valuePerDay / 8;
  }, [valuePerDay]);

  const timeSavedDays = useMemo(() => {
    if (!months || months <= 0) return null;
    return months * correctionFactor * 30;
  }, [months, correctionFactor]);

  const handleSave = async (navigateOnSuccess: boolean) => {
    if (!sessionId) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const updated = await updateBridgeSession(sessionId, {
        current_valuation: values.current_valuation,
        target_valuation: values.target_valuation,
        current_revenue: values.current_revenue ?? 0,
        current_expense: values.current_expense ?? 0,
        bridge_objective: values.bridge_objective?.trim() || undefined,
        target_months: values.estimated_months,
        commitment_toll_cost_1hr: values.cost_one_hour_value ?? undefined,
      });
      if (updated) {
        message.success('Objective saved');
        if (navigateOnSuccess) {
          navigate(`/bridge-session/${sessionId}?step=questions`);
        }
      } else {
        message.error('Failed to save');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please fill required fields');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommit = () => handleSave(true);

  if (!isLocalBridgeApiEnabled()) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="info"
          message="Local Bridge API not configured"
          description="Local Bridge API is not available. Ensure the backend is running."
        />
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          message="No session"
          description="Session ID is required. Go back to Local Bridge and create a session."
        />
        <Link to="/local-bridge" style={{ display: 'inline-block', marginTop: 16 }}>
          <ArrowLeftOutlined /> Back to Local Bridge
        </Link>
      </div>
    );
  }

  if (loading || !session) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/local-bridge" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ArrowLeftOutlined /> Back to Local Bridge
        </Link>
      </div>

      <h1 style={{ marginBottom: 24 }}>Business Objective</h1>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Define current state, future state, and transition. Commit to proceed to questions.
      </Text>

      <Form form={form} layout="vertical" initialValues={{ estimated_months: 12 }}>
        <section className={styles.bridgeObjectiveSection}>
          <div className={styles.bridgeStrip}>
            {/* Current state */}
            <div className={styles.bridgeZoneCurrent}>
              <div className={styles.bridgeZoneLabel}>Current state</div>
              <div className={styles.compactFields}>
                <div className={pageStyles.valueRatioPrimary}>
                  <span className={pageStyles.valueRatioLabel}>Value ratio</span>
                  <span className={pageStyles.valueRatioValue}>
                    {valueRatio != null ? valueRatio.toFixed(2) : '—'}
                  </span>
                </div>
                <div className={styles.compactRow} style={{ marginBottom: 4 }}>
                  <span className={styles.compactLabel}>Earnings</span>
                  <span>
                    {currentEarnings.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </span>
                </div>
                <div className={styles.compactRow} style={{ marginBottom: 8 }}>
                  <span className={styles.compactLabel}>Multiple of earnings</span>
                  <span>{currentMultiple != null ? `${currentMultiple.toFixed(1)}x` : '—'}</span>
                </div>
                <Form.Item
                  name="current_valuation"
                  label="Current valuation"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 500000" />
                </Form.Item>
                <Form.Item
                  name="current_revenue"
                  label="Current revenue"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 400000" />
                </Form.Item>
                <Form.Item
                  name="current_expense"
                  label="Current expense"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 350000" />
                </Form.Item>
              </div>
            </div>

            <div className={styles.bridgeConnector} aria-hidden>
              <span className={styles.bridgeConnectorArrow}>→</span>
              <span className={styles.bridgeConnectorText}>Bridge</span>
            </div>

            {/* Transition */}
            <div className={styles.bridgeZoneBridge}>
              <div className={styles.bridgeZoneLabel}>Transition</div>
              <div className={styles.compactFields}>
                <Form.Item
                  name="estimated_months"
                  label="Estimated months to transition"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g. 12" />
                </Form.Item>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Value/day</span>
                  <span>
                    {valuePerDay != null
                      ? valuePerDay.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '—'}
                  </span>
                </div>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Value/hour</span>
                  <span>
                    {valuePerHour != null
                      ? valuePerHour.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '—'}
                  </span>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div className={styles.compactRow} style={{ marginBottom: 8 }}>
                    <span className={styles.compactLabel}>Correction factor</span>
                    <span>{correctionFactorPercent}%</span>
                  </div>
                  <Slider
                    min={0}
                    max={10}
                    value={correctionFactorPercent}
                    onChange={setCorrectionFactorPercent}
                    tooltip={{ formatter: (v) => `${v}%` }}
                  />
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                    Projected improvement: revenue +{correctionFactorPercent}%, expense −{correctionFactorPercent}%
                  </Text>
                </div>
                <ImpactOfFrictionChart
                  revenue={currentRevenue}
                  expense={currentExpense}
                  height={90}
                />
                <div className={styles.compactRow} style={{ marginBottom: 8 }}>
                  <span className={styles.compactLabel}>Time saved</span>
                  <span>
                    {timeSavedDays != null && correctionFactor > 0
                      ? `${timeSavedDays.toFixed(0)} days`
                      : '—'}
                  </span>
                </div>
                <Form.Item
                  name="cost_one_hour_value"
                  label="Commitment toll (1 hr value)"
                  style={{ marginBottom: 16 }}
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="Optional" />
                </Form.Item>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    onClick={() => handleSave(false)}
                    loading={submitting}
                    style={{ flex: 1 }}
                  >
                    Save
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleCommit}
                    loading={submitting}
                    style={{ flex: 1 }}
                  >
                    Commit
                  </Button>
                </div>
              </div>
            </div>

            <div className={styles.bridgeConnector} aria-hidden>
              <span className={styles.bridgeConnectorArrow}>→</span>
              <span className={styles.bridgeConnectorText}>Future</span>
            </div>

            {/* Future state */}
            <div className={styles.bridgeZoneFuture}>
              <div className={styles.bridgeZoneLabel}>Future state</div>
              <div className={styles.compactFields}>
                <div className={pageStyles.valueRatioPrimary}>
                  <span className={pageStyles.valueRatioLabel}>Value ratio</span>
                  <span className={pageStyles.valueRatioValue}>
                    {futureValueRatio != null ? futureValueRatio.toFixed(2) : '—'}
                  </span>
                </div>
                {(currentRevenue > 0 || currentExpense > 0) && (
                  <>
                    <div className={styles.compactRow} style={{ fontSize: 11, marginBottom: 4 }}>
                      <span className={styles.compactLabel}>Earnings</span>
                      <span>
                        {futureEarnings.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                    <div className={styles.compactRow} style={{ fontSize: 11, marginBottom: 4 }}>
                      <span className={styles.compactLabel}>Multiple of earnings</span>
                      <span>{futureMultiple != null ? `${futureMultiple.toFixed(1)}x` : '—'}</span>
                    </div>
                    <div className={styles.compactRow} style={{ fontSize: 11, marginBottom: 4 }}>
                      <span className={styles.compactLabel}>Revenue</span>
                      <span>
                        {futureRevenue.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                    <div className={styles.compactRow} style={{ fontSize: 11, marginBottom: 8 }}>
                      <span className={styles.compactLabel}>Expense</span>
                      <span>
                        {futureExpense.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                  </>
                )}
                <Form.Item
                  name="bridge_objective"
                  label="Objective (next milestone)"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <TextArea rows={3} placeholder="e.g. Reach $2M ARR with positive unit economics" />
                </Form.Item>
                <Form.Item
                  name="target_valuation"
                  label="Future valuation"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 2000000" />
                </Form.Item>
              </div>
            </div>
          </div>
        </section>
      </Form>
    </div>
  );
};

export default BusinessObjectivePage;
